import { useMemo } from 'react';
import { isAgeRow, isDisabledColumn, generateCellKey } from '../utils';
import { GroupedRow } from 'modules/pond/api/types';

export interface ProcessedRow extends GroupedRow {
  showGroup: boolean;
  groupSpan: number;
  showSubGroup: boolean;
  subGroupSpan: number;
  isGroupMiddle: boolean;
  isSubGroupMiddle: boolean;
  isLastInGroup: boolean;
  isLastInSubGroup: boolean;
}

export const useProcessedRows = (
  rows: GroupedRow[] | undefined,
  editedCells: Map<string, string>
) => {
  const baseProcessedRows = useMemo(() => {
    if (!rows) return { baseRows: [], disabledColsMap: new Map<string, Set<number>>() };

    const disabledColsMap = new Map<string, Set<number>>();
    const groupStarts = new Map<string, number>();
    const groupSizes = new Map<string, number>();
    const subGroupStarts = new Map<string, number>();
    const subGroupSizes = new Map<string, number>();

    rows.forEach((row, idx) => {
      const groupKey = row.group;
      const subGroupKey = row.subGroup ? `${row.group}_${row.subGroup}` : row.group;

      if (!groupStarts.has(groupKey)) {
        groupStarts.set(groupKey, idx);
        groupSizes.set(groupKey, 0);
      }
      groupSizes.set(groupKey, groupSizes.get(groupKey)! + 1);

      if (!subGroupStarts.has(subGroupKey)) {
        subGroupStarts.set(subGroupKey, idx);
        subGroupSizes.set(subGroupKey, 0);
      }
      subGroupSizes.set(subGroupKey, subGroupSizes.get(subGroupKey)! + 1);
    });

    const processedRows: ProcessedRow[] = rows.map((row, idx) => {
      if (isAgeRow(row.label)) {
        const disabledCols = new Set<number>();
        row.values.forEach((val, i) => {
          if (isDisabledColumn(val)) {
            disabledCols.add(i);
          }
        });
        disabledColsMap.set(row.group, disabledCols);
      }

      const groupKey = row.group;
      const subGroupKey = row.subGroup ? `${row.group}_${row.subGroup}` : row.group;

      const groupStart = groupStarts.get(groupKey)!;
      const groupSize = groupSizes.get(groupKey)!;
      const subGroupStart = subGroupStarts.get(subGroupKey)!;
      const subGroupSize = subGroupSizes.get(subGroupKey)!;

      const posInGroup = idx - groupStart;
      const posInSubGroup = idx - subGroupStart;

      return {
        ...row,
        showGroup: posInGroup === 0,
        groupSpan: groupSize,
        showSubGroup: posInSubGroup === 0,
        subGroupSpan: subGroupSize,
        isGroupMiddle: posInGroup === Math.floor(groupSize / 2),
        isSubGroupMiddle: posInSubGroup === Math.floor(subGroupSize / 2),
        isLastInGroup: posInGroup === groupSize - 1,
        isLastInSubGroup: posInSubGroup === subGroupSize - 1,
      };
    });

    return { baseRows: processedRows, disabledColsMap };
  }, [rows]);

  const finalRows = useMemo(() => {
    return baseProcessedRows.baseRows.map(row => ({
      ...row,
      values: row.values.map((val, colIdx) => {
        const cellId = row.id[colIdx];
        const cellKey = generateCellKey(row.group, row.subGroup, row.label, cellId);
        const editedValue = editedCells.get(cellKey);
        return editedValue !== undefined ? editedValue : val;
      })
    }));
  }, [baseProcessedRows.baseRows, editedCells]);

  return { flatRows: finalRows, disabledColumns: baseProcessedRows.disabledColsMap };
};
